import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Mail, Monitor, Home, Building, Users } from 'lucide-react';
import Button from './Button';
import type { ServicePageContent } from '../../data/services';
import { callsToAction, practiceEmail, turnaround } from '../../data/practice';
type ServicePageTemplateProps = {
  content: ServicePageContent;
};
const assessmentFormats = [{
  icon: Monitor,
  label: 'Secure video assessment'
}, {
  icon: Users,
  label: 'Face-to-face assessment'
}, {
  icon: Home,
  label: 'Home and care setting visits'
}, {
  icon: Building,
  label: 'Prison and secure setting visits'
}];
const enquirySteps = ['Email a short outline: the type of proceedings, the court or tribunal, who is instructing, the questions you need answered, your deadlines and where the person is.', 'Dr Syed confirms whether the matter falls within his expertise and availability, and provides a fee quote and expected timescale. Not every instruction can be accepted.', 'Once terms are agreed, send the letter of instruction and records by the method agreed with Dr Syed. Please do not send medical records by ordinary email before then.', 'The assessment is arranged, and the report is prepared and delivered within the agreed timescale.'];
export default function ServicePageTemplate({
  content
}: ServicePageTemplateProps) {
  return <>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-415" data-mp="yywcqjl69pnw">
        <div className="container mx-auto px-4" data-id="element-416" data-mp="u0l42c6m5l79">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-417" data-mp="u1we2tqnjp6t">
            {content.heroTitle}
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-418" data-mp="1319hp5s3hjjm">
            {content.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center" data-id="element-419" data-mp="2jf9ggdf7f35a">
            <Button variant="secondary" href={callsToAction.discussInstruction.href} data-id="element-420" data-mp="2eggar765sgfo">
              {callsToAction.discussInstruction.label}
            </Button>
            <Button variant="transparent" href={callsToAction.requestFeeQuote.href} data-id="element-421" data-mp="1c2iemyswtdn3">
              {callsToAction.requestFeeQuote.label}
            </Button>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-422" data-mp="1l1vr80cbyic1">
        <div className="container mx-auto px-4" data-id="element-423" data-mp="1q3b95jj7o8q7">
          <div className="max-w-3xl mx-auto" data-id="element-424" data-mp="1q2065lxlb505">
            <p className="text-slate-dark text-lg mb-10" data-id="element-425" data-mp="td2be3pub72z">{content.intro}</p>

            {content.blocks.length > 1 && <nav aria-label="On this page" className="bg-gray-50 p-6 rounded-lg mb-10" data-id="element-426" data-mp="xexdhaflhoht">
                <p className="font-bold text-primary mb-3" data-id="element-427" data-mp="12c5wtechbykn">On this page</p>
                <ul className="space-y-2" data-id="element-428" data-mp="173nplf4cmsuj">
                  {content.blocks.map(block => <li key={block.id} data-id="element-429" data-mp="9ot74vb0van4">
                      <a href={`#${block.id}`} className="text-primary underline hover:text-secondary transition-colors" data-id="element-430" data-mp="9nruimecyg3a">
                        {block.title}
                      </a>
                    </li>)}
                </ul>
              </nav>}

            {content.blocks.map(block => {
            const Icon = block.icon;
            return <section key={block.id} id={block.id} aria-labelledby={`${block.id}-heading`} className="mb-12 scroll-mt-24" data-id="element-431" data-mp="3pa9nviejy8hu">
                  <div className="flex items-center mb-4" data-id="element-432" data-mp="3fivt1nxjpwv1">
                    <div className="text-primary mr-3 shrink-0" data-id="element-433" data-mp="3aktyo6uo6b49">
                      <Icon className="h-8 w-8" aria-hidden="true" data-id="element-434" data-mp="3aldjpwfm9fmi" />
                    </div>
                    <h2 id={`${block.id}-heading`} className="text-2xl font-serif font-bold text-primary" data-id="element-435" data-mp="35nc09aea3eai">
                      {block.title}
                    </h2>
                  </div>
                  <p className="text-slate-dark mb-6" data-id="element-436" data-mp="10hefzn2c8e8j">{block.intro}</p>
                  <div className={`grid grid-cols-1 gap-4 ${block.questions.length > 0 ? 'md:grid-cols-2' : ''}`} data-id="element-437" data-mp="10kaebci3l4xi">
                    <div className="bg-gray-50 p-5 rounded-lg" data-id="element-438" data-mp="qsd1607j42w5">
                      <h3 className="font-bold text-primary mb-3" data-id="element-439" data-mp="qruyakqd2qf9">
                        Instructions Dr Syed considers
                      </h3>
                      <ul className="space-y-2 text-slate-dark" data-id="element-440" data-mp="gtffd51hmngu">
                        {block.instructions.map(item => <li key={item} className="flex items-start" data-id="element-441" data-mp="lttchs1jwd03">
                            <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-0.5 shrink-0" aria-hidden="true" data-id="element-442" data-mp="7tsgapw8dtqy" />
                            <span data-id="element-443" data-mp="ctdbcp8a5hye">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                    {block.questions.length > 0 && <div className="bg-gray-50 p-5 rounded-lg" data-id="element-444" data-mp="2751xpcgucmst">
                        <h3 className="font-bold text-primary mb-3" data-id="element-445" data-mp="2h9s2dq3mq50q">
                          Questions a report may address
                        </h3>
                        <ul className="space-y-2 text-slate-dark" data-id="element-446" data-mp="2c940289ya1r7">
                          {block.questions.map(item => <li key={item} className="flex items-start" data-id="element-447" data-mp="2lc55s7uwlquc">
                              <span className="text-secondary font-bold mr-2" aria-hidden="true" data-id="element-448" data-mp="2l9sf96b2y85o">•</span>
                              <span data-id="element-449" data-mp="302m5nmrw48p4">{item}</span>
                            </li>)}
                        </ul>
                      </div>}
                  </div>
                  {block.note && <p className="text-slate-dark mt-4" data-id="element-450" data-mp="34zm9rs56j1dl">{block.note}</p>}
                </section>;
          })}

            {/* Assessment format */}
            <section aria-labelledby="assessment-format-heading" className="mb-12" data-id="element-451" data-mp="1amcypvwzj7t8">
              <h2 id="assessment-format-heading" className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-452" data-mp="39qmm8tddhe94">
                Assessment Format
              </h2>
              <p className="text-slate-dark mb-6" data-id="element-453" data-mp="3jp2e4yk84rjo">{content.assessmentNote}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-454" data-mp="3jr58fuset6u7">
                {assessmentFormats.map(({
                icon: Icon,
                label
              }) => <li key={label} className="flex items-center bg-gray-50 p-4 rounded-lg text-slate-dark" data-id="element-455" data-mp="3sq8rwzvhno98">
                    <Icon className="h-6 w-6 text-primary mr-3 shrink-0" aria-hidden="true" data-id="element-456" data-mp="1i4j1l44px8b" />
                    <span data-id="element-457" data-mp="6axk9z0e2nfn">{label}</span>
                  </li>)}
              </ul>
              <p className="text-slate text-sm mt-3" data-id="element-458" data-mp="b7xqc9x8nq6r">
                The format is agreed for each case, depending on what is
                clinically appropriate and practical.
              </p>
            </section>

            {/* Turnaround */}
            <section aria-labelledby="turnaround-heading" className="mb-12 border-l-4 border-primary bg-gray-50 p-6 rounded-lg" data-id="element-459" data-mp="k7b1d710gmtl">
              <div className="flex items-center mb-3" data-id="element-460" data-mp="20ojm2l0ey4ud">
                <Clock className="h-6 w-6 text-primary mr-2 shrink-0" aria-hidden="true" data-id="element-461" data-mp="1vqi2lyz2s3id" />
                <h2 id="turnaround-heading" className="text-xl font-serif font-bold text-primary" data-id="element-462" data-mp="1qs6rzm0yhoz3">
                  Turnaround
                </h2>
              </div>
              <ul className="space-y-1 text-slate-dark font-medium mb-3" data-id="element-463" data-mp="2ohrwlxigbefq">
                <li data-id="element-464" data-mp="2jh49vo199jrb">{turnaround.urgent}</li>
                <li data-id="element-465" data-mp="2epvywnka1qc1">{turnaround.standard}</li>
              </ul>
              <p className="text-slate-dark text-sm" data-id="element-466" data-mp="2eo1dl853fw7f">{turnaround.caveat}</p>
            </section>

            {/* How to enquire */}
            <section aria-labelledby="enquire-heading" className="mb-12" data-id="element-467" data-mp="3766pkqcbx2j6">
              <h2 id="enquire-heading" className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-468" data-mp="3789o5yrjs3zi">
                How to Make an Enquiry
              </h2>
              <ol className="space-y-4" data-id="element-469" data-mp="2apsokrhgqzfz">
                {enquirySteps.map((step, index) => <li key={step} className="flex items-start" data-id="element-470" data-mp="2fkotgs7sthai" data-mpi={index}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white font-bold mr-4" aria-hidden="true" data-id="element-471" data-mp="21sic52swt49h">
                      {index + 1}
                    </span>
                    <span className="text-slate-dark pt-1" data-id="element-472" data-mp="2yhq16ksqlvi5">{step}</span>
                  </li>)}
              </ol>
              <p className="text-slate-dark mt-6" data-id="element-473" data-mp="2kh6r784pofdc">
                Dr Syed’s overriding duty is to the court. His opinion is
                independent of the party instructing him.
              </p>
            </section>

            {/* Related Services */}
            <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-474" data-mp="1e25x77z8y79e">
              <h2 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-475" data-mp="1dz9yvijhlgkf">
                Related Services
              </h2>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-476" data-mp="11jk0ksvyizh8">
                {content.related.map(item => <Link key={item.path} to={item.path} className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-477" data-mp="1fhgzvx1spjzl">
                    {item.label}
                  </Link>)}
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-md text-center" data-id="element-478" data-mp="hsyk7ny2bmw8">
              <h2 className="text-xl font-serif font-bold text-primary mb-3" data-id="element-479" data-mp="wlikmgttvg9z">
                Discuss an Instruction
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-480" data-mp="43mvweg96308">
                Send a short outline of the case and Dr Syed will confirm
                whether he can help, with a fee quote and timescale.
              </p>
              <p className="mb-6" data-id="element-481" data-mp="1h4kgzibdtkkk">
                <a href={`mailto:${practiceEmail}`} className="inline-flex items-center text-primary hover:text-secondary transition-colors break-all" data-id="element-482" data-mp="1h3j3z8at2o07">
                  <Mail className="h-5 w-5 mr-2 shrink-0" aria-hidden="true" data-id="element-483" data-mp="17538yu8nqulf" />
                  {practiceEmail}
                </a>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-484" data-mp="y5pz40gmg406">
                <Button variant="primary" href={callsToAction.discussInstruction.href} data-id="element-485" data-mp="y7sznefehfj5">
                  {callsToAction.discussInstruction.label}
                </Button>
                <Button variant="secondary" href={callsToAction.requestFeeQuote.href} data-id="element-486" data-mp="ogohk5ufhek3">
                  {callsToAction.requestFeeQuote.label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}